import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import MatchNotificationItem from "./matchNotificationItem";
import { NotificationContext } from "../../../createContext/Notification/NotificationContext";
import styles from "./matchNotification.module.css";
import isAuth from "../../../createContext/is-Auth/IsAuthContext";

const Backend_URL = process.env.REACT_APP_BACKEND_URL;

const MatchNotification = () => {
  const { token } = useContext(isAuth);
  const { allMatches, loading, error, user } = useContext(NotificationContext);
  const [sports, setSports] = useState([]);
  const [loadingSports, setLoadingSports] = useState(true);
  const [creators, setCreators] = useState({});
  const [adminsArr, setAdminsArr] = useState({});
  const [loadingCreators, setLoadingCreators] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  // Memoize the API calls
  const fetchData = useCallback(async (url, options = {}) => {
    try {
      const response = await axios.get(url, options);
      return response.data;
    } catch (err) {
      console.error("Error fetching data:", err);
      return null;
    }
  }, []);

  // Fetch sports only once
  useEffect(() => {
    const fetchSports = async () => {
      setLoadingSports(true);
      const data = await fetchData(`${Backend_URL}/api/sport/`);
      setSports(data || []);
      setLoadingSports(false);
    };
    fetchSports();
  }, [fetchData]);

  // Batch fetch creators and admins
  useEffect(() => {
    const fetchAllCreatorsAndAdmins = async () => {
      if (!allMatches?.length) return;

      const creatorIds = [...new Set(allMatches.map(match => match.creator))];
      const adminIds = [...new Set(allMatches.flatMap(match => match.admins))];
      
      // Filter out IDs we already have
      const newCreatorIds = creatorIds.filter(id => !creators[id]);
      const newAdminIds = adminIds.filter(id => !adminsArr[id]);

      if (!newCreatorIds.length && !newAdminIds.length) return;

      setLoadingCreators(true);
      setLoadingAdmins(true);

      // Batch fetch creators
      const creatorPromises = newCreatorIds.map(id => 
        fetchData(`${Backend_URL}/api/users/${id}`, { headers: { token } })
      );
      
      // Batch fetch admins
      const adminPromises = newAdminIds.map(id => 
        fetchData(`${Backend_URL}/api/users/${id}`, { headers: { token } })
      );

      const [creatorResults, adminResults] = await Promise.all([
        Promise.all(creatorPromises),
        Promise.all(adminPromises)
      ]);

      // Update states with new data
      const newCreators = Object.fromEntries(
        creatorResults.map((data, i) => [newCreatorIds[i], data]).filter(([, data]) => data)
      );
      
      const newAdmins = Object.fromEntries(
        adminResults.map((data, i) => [newAdminIds[i], data]).filter(([, data]) => data)
      );

      setCreators(prev => ({ ...prev, ...newCreators }));
      setAdminsArr(prev => ({ ...prev, ...newAdmins }));

      setLoadingCreators(false);
      setLoadingAdmins(false);
    };

    fetchAllCreatorsAndAdmins();
  }, [allMatches, creators, adminsArr, token, fetchData]);

  // Memoize formatTime function
  const formatTime = useCallback((timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const differenceInMs = now - notificationTime;
    const differenceInMinutes = Math.floor(differenceInMs / 60000);

    if (differenceInMinutes < 1) return "Just now";
    if (differenceInMinutes < 60) return `${differenceInMinutes} min ago`;
    
    if (differenceInMinutes < 1440) {
      const hours = notificationTime.getHours();
      const minutes = notificationTime.getMinutes();
      const period = hours >= 12 ? "pm" : "am";
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = String(minutes).padStart(2, "0");
      return `${formattedHours}:${formattedMinutes} ${period}`;
    }
    
    const day = notificationTime.getDate();
    const month = notificationTime.toLocaleString("default", { month: "short" });
    return `${day} ${month}`;
  }, []);

  // Memoize getSportName function
  const getSportName = useCallback((sportId) => {
    return sports.find(sport => sport._id === sportId)?.name || "Unknown Sport";
  }, [sports]);

  // Memoize sorted matches
  const sortedMatches = useMemo(() => {
    return allMatches ? 
      [...allMatches].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) 
      : [];
  }, [allMatches]);

  if (loading || loadingSports || loadingCreators || loadingAdmins) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      {sortedMatches.length > 0 ? (
        sortedMatches.map((match, index) => {
          const {
            _id,
            date,
            location,
            sport,
            teams,
            isTeamMatch,
            name,
            status,
            admins,
            createdAt,
            creator,
            scores,
            isFiltered
          } = match;

          const creatorData = creators[creator];
          const sportName = getSportName(sport);
          const userRole = admins.includes(user?._id) ? "admin" : "player";
          const adminData1 = adminsArr[admins[0]];
          const adminData2 = adminsArr[admins[1]];
          
          const matchDate = new Date(date);
          
          return (
            <React.Fragment key={_id}>
              <MatchNotificationItem
                matchId={_id}
                location={location}
                date={matchDate.toLocaleDateString()}
                time={matchDate.toLocaleTimeString()}
                player1={adminData1?.name || "Admin 1"}
                player2={adminData2?.name || "Admin 2"}
                scores={scores}
                sportName={sportName}
                isTeamMatch={isTeamMatch}
                team1Id={isTeamMatch ? teams?.[0]?.team : null}
                team2Id={isTeamMatch ? teams?.[1]?.team : null}
                matchName={name}
                matchStatus={status}
                userRole={userRole}
                timestamp={formatTime(createdAt)}
                creatorAvatar={creatorData?.avatar || " "}
                creatorName={creatorData?.name || " "}
                isFiltered={isFiltered}
              />
              {index < sortedMatches.length - 1 && (
                <div className={styles.matchDivider} />
              )}
            </React.Fragment>
          );
        })
      ) : (
        <div>No matches found</div>
      )}
    </div>
  );
};

export default React.memo(MatchNotification);